import { Button } from '~/components/shadcn/Button';
import { Skeleton } from '~/components/shadcn/Skeleton';
import { AuiIf, ThreadListItemMorePrimitive, ThreadListItemPrimitive, ThreadListPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { MoreHorizontalIcon, PencilIcon, PlusIcon, TrashIcon, XIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FC } from 'react';

export const ThreadList: FC = () => {
	return (
		<ThreadListPrimitive.Root className="aui-root aui-thread-list-root flex flex-col gap-1">
			<ThreadListNew />
			{/* @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node" */}
			<AuiIf condition={(s) => s.threads.isLoading}>
				<ThreadListSkeleton />
			</AuiIf>
			{/* @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node" */}
			<AuiIf condition={(s) => !s.threads.isLoading}>
				<ThreadListPrimitive.Items components={{ ThreadListItem }} />
			</AuiIf>
		</ThreadListPrimitive.Root>
	);
};

const ThreadListNew: FC = () => {
	return (
		<ThreadListPrimitive.New asChild>
			<Button className="aui-thread-list-new h-9 w-full justify-start gap-2 rounded-lg px-3 text-sm">
				<PlusIcon className="size-4" />
				New Thread
			</Button>
		</ThreadListPrimitive.New>
	);
};

const ThreadListSkeleton: FC = () => {
	return (
		<div className="flex flex-col gap-1">
			{Array.from({ length: 5 }, (_, i) => (
				<div key={i} role="status" aria-label="Loading threads" className="aui-thread-list-skeleton-wrapper flex h-9 items-center px-3">
					<Skeleton className="aui-thread-list-skeleton h-4 w-full" />
				</div>
			))}
		</div>
	);
};

const RenamingContext = createContext<{
	isRenaming: boolean;
	setIsRenaming: (v: boolean) => void;
}>({ isRenaming: false, setIsRenaming: () => {} });

const ThreadListItem: FC = () => {
	const [isRenaming, setIsRenaming] = useState(false);

	return (
		<RenamingContext.Provider value={{ isRenaming, setIsRenaming }}>
			<ThreadListItemPrimitive.Root className="aui-thread-list-item group flex h-9 items-center gap-2 rounded-lg transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none data-active:bg-muted">
				{isRenaming ? (
					<ThreadListItemRenameInput />
				) : (
					<ThreadListItemPrimitive.Trigger className="aui-thread-list-item-trigger flex h-full min-w-0 flex-1 items-center px-3 text-start text-sm">
						<span className="aui-thread-list-item-title min-w-0 flex-1 truncate">
							<ThreadListItemPrimitive.Title fallback="New Chat" />
						</span>
					</ThreadListItemPrimitive.Trigger>
				)}
				{!isRenaming && <ThreadListItemMore />}
			</ThreadListItemPrimitive.Root>
		</RenamingContext.Provider>
	);
};

const ThreadListItemRenameInput: FC = () => {
	const aui = useAui();
	// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
	const currentTitle = useAuiState((s) => s.threadListItem.title) ?? '';
	const [value, setValue] = useState(currentTitle);
	const inputRef = useRef<HTMLInputElement>(null);
	const { setIsRenaming } = useContext(RenamingContext);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const handleSubmit = useCallback(() => {
		const trimmed = value.trim();
		if (trimmed && trimmed !== currentTitle) {
			// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
			aui.threadListItem()?.rename(trimmed);
		}
		setIsRenaming(false);
	}, [aui, currentTitle, setIsRenaming, value]);

	const handleCancel = useCallback(() => setIsRenaming(false), [setIsRenaming]);

	return (
		<div className="flex h-full min-w-0 flex-1 items-center">
			<input
				ref={inputRef}
				className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm outline-none"
				value={value}
				onBlur={handleCancel}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') handleSubmit();
					if (e.key === 'Escape') {
						e.nativeEvent.stopImmediatePropagation();
						handleCancel();
					}
				}}
			/>
			<button
				className="mr-2 rounded p-0.5 text-destructive hover:bg-destructive/10"
				onMouseDown={(e) => e.preventDefault()}
				onClick={handleCancel}
			>
				<XIcon className="size-4" />
				<span className="sr-only">Cancel rename</span>
			</button>
		</div>
	);
};

const ThreadListItemRenameMenuItem: FC = () => {
	const { setIsRenaming } = useContext(RenamingContext);
	return (
		<ThreadListItemMorePrimitive.Item
			className="aui-thread-list-item-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
			onClick={() => setIsRenaming(true)}
		>
			<PencilIcon className="size-4" />
			Rename
		</ThreadListItemMorePrimitive.Item>
	);
};

const ThreadListItemMore: FC = () => {
	return (
		<ThreadListItemMorePrimitive.Root>
			<ThreadListItemMorePrimitive.Trigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="aui-thread-list-item-more mr-2 size-7 p-0 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:bg-accent data-[state=open]:opacity-100 group-data-active:opacity-100"
				>
					<MoreHorizontalIcon className="size-4" />
					<span className="sr-only">More options</span>
				</Button>
			</ThreadListItemMorePrimitive.Trigger>
			<ThreadListItemMorePrimitive.Content
				side="bottom"
				align="start"
				className="aui-thread-list-item-more-content z-[10000] min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
			>
				<ThreadListItemRenameMenuItem />
				<ThreadListItemPrimitive.Delete asChild>
					<ThreadListItemMorePrimitive.Item className="aui-thread-list-item-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-destructive text-sm outline-none hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive">
						<TrashIcon className="size-4" />
						Delete
					</ThreadListItemMorePrimitive.Item>
				</ThreadListItemPrimitive.Delete>
			</ThreadListItemMorePrimitive.Content>
		</ThreadListItemMorePrimitive.Root>
	);
};
